import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { MarketingChrome } from "../components/MarketingChrome";
import { IconClock, IconMail, IconShield } from "../components/Icons";
import { contact as contactApi } from "../api/endpoints";
import { validateEmail } from "../lib/validation";

export function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [topicError, setTopicError] = useState<string | null>(null);
  const [messageError, setMessageError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nameProblem = name.trim() ? null : "Enter your name";
    const emailProblem = validateEmail(email);
    const topicProblem = topic ? null : "Choose what we can help with";
    const messageProblem = message.trim() ? null : "Enter a message";
    setNameError(nameProblem);
    setEmailError(emailProblem);
    setTopicError(topicProblem);
    setMessageError(messageProblem);
    setSubmitError(null);
    if (nameProblem || emailProblem || topicProblem || messageProblem) return;

    setSubmitting(true);
    try {
      await contactApi.send({
        name: name.trim(),
        email: email.trim(),
        message: `${topic}\n\n${message.trim()}`,
        website,
      });
      setSent(true);
    } catch (err) {
      setSubmitError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <MarketingChrome back={{ to: "/", label: "Back to Inputry" }}>
      <div className="apply-page">
        <div className="apply-layout contact-page">
          <aside className="apply-pitch">
            <div className="kicker"><i /> Support</div>
            <h1>Contact Inputry</h1>
            <p className="sub">
              Questions about applying, payments or your contributor account? Send us a message and a
              real person on our team will reply.
            </p>
            <div className="stack">
              <div className="soft-card">
                  <div className="soft-icon">
                    <IconMail width={16} height={16} />
                  </div>
                  <div className="soft-icon-text">
                    <h3 style={{ marginTop: 0 }}> Email support</h3>
                    <p>team@inputry.com</p>
                  </div>
              </div>
              <div className="soft-card">
                <div className="soft-icon">
                  <IconClock width={16} height={16} />
                </div>
                <div className="soft-icon-text">
                  <h3 style={{ marginTop: 0 }}> Response time</h3>
                  <p>We aim to reply within 2 business days</p>
                </div>
              </div>
              <div className="soft-card privacy-card">
                <div className="soft-icon">
                  <IconShield width={16} height={16} />
                </div>
                <div className="soft-icon-text">
                  <h3 style={{ marginTop: 0 }}> Privacy & terms</h3>
                  <p>
                    <Link to="/privacy">Read how we handle your information</Link>
                  </p>
                </div>
              </div>
            </div>
          </aside>
          <form className="panel-card" onSubmit={handleSubmit} noValidate>
            <h2 style={{ marginTop: 0 }}>Send us a message</h2>
            {sent ? (
              <p className="sub" role="status">
                Thanks, {name.trim()}. Your message is with the team, and we'll reply to {email.trim()}.
              </p>
            ) : (
              <>
                <div className={nameError ? "field err" : "field"}>
                  <label htmlFor="contact-name">Full name</label>
                  <input
                    id="contact-name"
                    name="name"
                    placeholder="Your full name"
                    autoComplete="name"
                    value={name}
                    onChange={(event) => {
                      setName(event.target.value);
                      setNameError(null);
                    }}
                  />
                  <div className="err-msg">{nameError}</div>
                </div>
                <div className={emailError ? "field err" : "field"}>
                  <label htmlFor="contact-email">Email address</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    placeholder="Enter email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      setEmailError(null);
                    }}
                  />
                  <div className="err-msg">{emailError}</div>
                </div>
                <div className={topicError ? "field err" : "field"}>
                  <label htmlFor="contact-topic">What can we help with?</label>
                  <select
                    id="contact-topic"
                    name="topic"
                    value={topic}
                    onChange={(event) => {
                      setTopic(event.target.value);
                      setTopicError(null);
                    }}
                  >
                    <option value="" disabled>Select</option>
                    <option>Applying</option>
                    <option>Payments</option>
                    <option>My contributor account</option>
                    <option>Something else</option>
                  </select>
                  <div className="err-msg">{topicError}</div>
                </div>
                <div className={messageError ? "field err" : "field"}>
                  <label htmlFor="contact-message">Message</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    placeholder="Tell us a little more..."
                    value={message}
                    onChange={(event) => {
                      setMessage(event.target.value);
                      setMessageError(null);
                    }}
                  />
                  <div className="err-msg">{messageError}</div>
                </div>
                <div className="apply-trap" aria-hidden="true">
                  <label htmlFor="contact-website">Website</label>
                  <input
                    id="contact-website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(event) => setWebsite(event.target.value)}
                  />
                </div>
                {submitError && <div className="form-error">{submitError}</div>}
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? "Sending…" : "Send message"}
                </button>
                <p className="fine">We only use your details to respond to your message.</p>
              </>
            )}
          </form>
        </div>
      </div>
    </MarketingChrome>
  );
}
